import React from "react";
import {
    Card,
    CardBody,
    CardFooter,
    Avatar,
    Tooltip,
    Chip,
    Button,
} from "@heroui/react";
import {
    PencilIcon,
    TrashIcon,
    MapPinIcon,
    CalendarIcon,
} from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import { MarketplaceCardResponseDto, ReadyRosterCardDto } from "../../../types/types";
import { formatPurseText, getCurrencySymbol, getSportColor } from "../../../utils/opportunities/opportunityAndReadyRoster";
import { formatSportName, formatWeightClassName } from "../../../utils/utilMethods";
import { formatDate } from "../../../utils/common/formatUtils";

interface FighterCardProps {
    item: MarketplaceCardResponseDto;
    role: string;
    activeTab: "all" | "myCards";
    onView: (item: MarketplaceCardResponseDto) => void;
    onEdit: (item: MarketplaceCardResponseDto) => void;
    onDelete: (id: string) => void;
}

const FighterCard: React.FC<FighterCardProps> = ({
    item,
    role,
    activeTab,
    onView,
    onEdit,
    onDelete,
}) => {
    const { t } = useTranslation('readyRoster');
    const fighter = item.readyRosterCard?.fighter;
    const isPublished = item.readyRosterCard?.status === "PUBLISH";
    const isExpired = new Date(item.readyRosterCard?.validUpto) < new Date();

    let cardBgClass = "bg-white";

    if (activeTab === "all") {
        cardBgClass = isExpired ? "bg-red-50" : "bg-white";
    } else if (activeTab === "myCards") {
        if (isExpired) {
            cardBgClass = "bg-red-50";
        } else if (isPublished) {
            cardBgClass = "bg-gray-50";
        } else {
            cardBgClass = "bg-white";
        }
    }

    return (
        <Card
            key={item.id}
            className={`${cardBgClass} shadow-sm transition-shadow border border-gray-200 h-[185px] w-full group relative overflow-hidden`}
        >
            <CardBody
                className="p-2 cursor-pointer h-[180px] relative group"
                onClick={() => onView(item)}
            >
                <div className="p-1 border-b border-gray-100">
                    <div className="flex items-start gap-3">
                        <Avatar
                            src={fighter?.profileImage || undefined}
                            name={
                                !fighter?.profileImage &&
                                    fighter?.firstName &&
                                    fighter?.lastName
                                    ? `${fighter.firstName.charAt(0)}${fighter.lastName.charAt(0)}`
                                    : "Fighter"
                            }
                            size="sm"
                            className="bg-blue-100 text-blue-600"
                        />
                        <div className="flex flex-col flex-1">
                            <div className="flex justify-between items-start">
                                <h3
                                    className="font-semibold text-gray-900 text-sm truncate"
                                    title={`${fighter?.firstName} ${fighter?.lastName}`}
                                >
                                    {`${fighter?.firstName ?? ""} ${fighter?.lastName ?? ""}`}
                                </h3>

                                {role === "FIGHTER" &&
                                    activeTab === "myCards" &&
                                    item.readyRosterCard?.status !== "PUBLISH" && (
                                        <div className="flex gap-1 opacity-0 opacity-100">
                                            <Tooltip content={t("edit")}>
                                                <Button
                                                    isIconOnly
                                                    size="sm"
                                                    variant="light"
                                                    onPress={() => onEdit(item)}
                                                    className="h-7 w-7 text-blue-600 hover:bg-blue-50 rounded-full"
                                                >
                                                    <PencilIcon className="w-4 h-4" />
                                                </Button>
                                            </Tooltip>
                                            <Tooltip content={t("delete")} color="danger">
                                                <Button
                                                    isIconOnly
                                                    size="sm"
                                                    variant="light"
                                                    onPress={() =>
                                                        item.id && onDelete(item.id.toString())
                                                    }
                                                    className="h-7 w-7 text-red-600 hover:bg-red-50 rounded-full"
                                                >
                                                    <TrashIcon className="w-4 h-4" />
                                                </Button>
                                            </Tooltip>
                                        </div>
                                    )}
                            </div>

                            <div className="flex flex-wrap items-center gap-2 mt-1">
                                <Tooltip content={t("sport")}>
                                    <Chip
                                        color={getSportColor(item.readyRosterCard?.sport ?? "")}
                                        variant="flat"
                                        size="sm"
                                        className="text-xs font-medium"
                                    >
                                        {formatSportName(item.readyRosterCard?.sport?? "")}
                                    </Chip>
                                </Tooltip>

                                <Tooltip content={t("purse")}>
                                    <Chip
                                        color="default"
                                        variant="flat"
                                        size="sm"
                                        className="text-xs font-medium"
                                    >
                                        {formatPurseText(item.readyRosterCard?.purse ?? "")}{" "}
                                        {getCurrencySymbol(item.readyRosterCard?.currency ?? "")}
                                    </Chip>
                                </Tooltip>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="pt-2 space-y-2">
                    <div className="flex items-center gap-1 text-sm">
                        <Tooltip content={t("weight_class")}>
                            <Chip
                                color="success"
                                variant="flat"
                                size="sm"
                                className="text-xs"
                            >
                                {formatWeightClassName(item.readyRosterCard?.weightClass ?? "")}
                            </Chip>
                        </Tooltip>

                        <Tooltip content={t("preferred_ruleset")}>
                            <Chip
                                color="default"
                                variant="flat"
                                size="sm"
                                className="text-xs"
                            >
                                {formatSportName(item.readyRosterCard?.preferredRuleSet ?? "")}
                            </Chip>
                        </Tooltip>
                    </div>

                    {item.readyRosterCard?.location && (
                        <div className="flex items-center gap-1 text-sm text-gray-600 font-medium">
                            <MapPinIcon className="w-4 h-4 text-gray-500" />
                            <Tooltip content={t("location")}>{item.readyRosterCard?.location}</Tooltip>
                        </div>
                    )}
                </div>
            </CardBody>

            <CardFooter className="bg-gray-50 px-4 py-3 border-t border-gray-100">
                <div className="flex items-center justify-between w-full text-xs text-gray-500">
                    <div className="flex items-center gap-2">
                        <CalendarIcon className="w-3.5 h-3.5" />
                        <span>{t("valid_upto")}{formatDate(item.readyRosterCard?.validUpto)}</span>
                    </div>

                    <div className="flex gap-2">
                        {activeTab === "myCards" && (
                            <>
                                {item.readyRosterCard?.status === "PUBLISH" && (
                                    <span className="text-xs font-semibold text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
                                        {t("published")}
                                    </span>
                                )}
                                {item.readyRosterCard?.status === "AVAILABLE" && (
                                    <span className="text-xs font-semibold text-blue-700 bg-blue-100 px-2 py-0.5 rounded-full">
                                        {t("available")}
                                    </span>
                                )}
                                {item.readyRosterCard?.validUpto &&
                                    new Date(item.readyRosterCard?.validUpto) < new Date() && (
                                        <span className="text-xs font-semibold text-red-700 bg-red-100 px-2 py-0.5 rounded-full">
                                            {t("expired")}
                                        </span>
                                    )}
                            </>
                        )}
                    </div>
                </div>
            </CardFooter>
        </Card>
    );
};

export default FighterCard;
